import React, { useState } from 'react';
import AdvancedPipelineControls from './AdvancedPipelineControls';
import ConfirmDialog, { TEAL, ENRICH_DEFAULTS, useEnrichAction } from './EnrichConfirmDialog';
import { totalActionable, actionableByEntity } from '../../lib/composition';
import { lastImport, recentActivity, SOURCE } from '../../lib/dataSource';

// The step-picker checklist uses the shared ENRICH_STEPS (all ticked by default).
// Deselecting an upstream step (country/website) is allowed but can lower downstream
// fill quality, since later steps read earlier ones as input evidence. The internal
// 'global' registry step is deliberately absent from ENRICH_STEPS, so it never shows in
// the picker — yet still runs on a full recipe run (all ticked → `steps` omitted →
// the backend runs the entire recipe, global included).

// The user's whole mental model here is "SignalCue fills the gaps in my data." So the
// page leads with one action — Enrich my data — and keeps the operator levers
// (provider, caps, single-step runs) folded away under Advanced. The spend gate and
// caps live in EnrichConfirmDialog, shared with the Quality-tab card.
const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const ENTITY_LABEL = { companies: 'companies', people: 'people', jobs: 'jobs' };

function relTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString();
}

function StatusDot({ status }) {
  const c = status === 'success' ? '#1baf7a' : status === 'failed' ? '#dc2626' : '#eda100';
  const label = status === 'success' ? 'Done' : status === 'failed' ? 'Failed' : 'Running';
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: c }} />
      {label}
    </span>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-lg font-semibold text-gray-900 tabular-nums">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

export default function PipelinePanel({ runs, coverage, onRunEnrichment, onRunEnricher, onStartImport, busy, job }) {
  const [advanced, setAdvanced] = useState(false);
  const { confirming, setConfirming, selectedSteps, toggleStep, runEnrich } = useEnrichAction(onRunEnrichment);

  const gaps = totalActionable(coverage);
  const breakdown = actionableByEntity(coverage);
  const imported = lastImport(runs);
  const activity = recentActivity(runs);
  const jobRunning = job?.status === 'running';
  const jobActive = jobRunning || job?.status === 'paused';

  // "in people / in companies" — these are gaps LOCATED in each record type, not counts
  // of records. Saying "44,341 companies" beside a source card reading "13,601
  // companies" reads as a contradiction; "44,341 in companies" does not.
  const breakdownText = breakdown
    .slice(0, 3)
    .map((b) => `${fmt(b.actionable)} in ${ENTITY_LABEL[b.entity] || b.entity}`)
    .join(' · ');

  return (
    <div className="space-y-6">
      {/* ── Hero: the one action ─────────────────────────────────────────── */}
      <section
        className="rounded-2xl border p-6 sm:p-8"
        style={{ background: 'linear-gradient(135deg,#f0fbf9 0%,#ffffff 60%)', borderColor: '#cdeee8' }}
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-gray-900">Enrich your data</h2>
            {gaps > 0 ? (
              <>
                <p className="text-sm text-gray-600 mt-1.5 max-w-xl">
                  SignalCue can fill <strong className="text-gray-900">{fmt(gaps)}</strong> empty fields across your
                  records — finding websites, industries, locations and LinkedIn pages your ATS never had.
                </p>
                {breakdownText && <p className="text-xs text-gray-500 mt-2 tabular-nums">{breakdownText}</p>}
              </>
            ) : (
              <p className="text-sm text-gray-600 mt-1.5 max-w-xl">
                Your records are as complete as SignalCue can make them right now. Import more data to find new
                gaps to fill.
              </p>
            )}
          </div>
          <div className="flex-none">
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={busy || gaps === 0 || jobActive}
              className="px-6 py-3 rounded-xl text-sm font-semibold text-white shadow-sm disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{ background: TEAL, '--tw-ring-color': TEAL }}
            >
              {busy || jobRunning ? 'Enriching…' : job?.status === 'paused' ? 'Paused' : 'Enrich my data'}
            </button>
            <p className="text-[11px] text-gray-400 mt-2 text-center">Uses AI · we&apos;ll confirm first</p>
          </div>
        </div>
      </section>

      {/* ── Data source ──────────────────────────────────────────────────── */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Your data source</h3>
            <p className="text-sm text-gray-700 mt-1">
              {SOURCE.name} <span className="text-xs text-gray-400">· {SOURCE.kind}</span>
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Last imported {relTime(imported?.at)}
              {SOURCE.liveSyncAvailable ? '' : ' · live sync coming soon'}
            </p>
          </div>
          <button
            type="button"
            onClick={onStartImport}
            disabled={busy}
            className="text-xs font-medium text-[#007A6B] hover:underline disabled:opacity-60 disabled:no-underline"
          >
            {busy ? 'Working…' : 'Refresh data'}
          </button>
        </div>
        {imported && (imported.companies != null || imported.people != null || imported.jobs != null) ? (
          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
            <Stat label="Companies" value={fmt(imported.companies)} />
            <Stat label="People" value={fmt(imported.people)} />
            <Stat label="Jobs" value={fmt(imported.jobs)} />
          </div>
        ) : null}
      </section>

      {/* ── Recent activity ──────────────────────────────────────────────── */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent activity</h3>
        {activity.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing has run yet.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {activity.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 py-2.5">
                <span className="text-sm text-gray-800">{a.action}</span>
                <span className="flex items-center gap-4">
                  <StatusDot status={a.status} />
                  <span className="text-xs text-gray-400 tabular-nums w-24 text-right">{relTime(a.at)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Advanced (the old admin machinery) ───────────────────────────── */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-100">
        <button
          type="button"
          onClick={() => setAdvanced((v) => !v)}
          aria-expanded={advanced}
          className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
        >
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Advanced controls</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Choose a provider, set caps, or run a single enricher. For operators.
            </p>
          </div>
          <span className="text-xs text-gray-500 flex-none">{advanced ? 'Hide' : 'Show'}</span>
        </button>
        {advanced && (
          <div className="px-5 pb-5 border-t border-gray-100 pt-5">
            <AdvancedPipelineControls
              onRunEnrichment={onRunEnrichment}
              onRunEnricher={onRunEnricher}
              onStartImport={onStartImport}
              busy={busy}
            />
          </div>
        )}
      </section>

      <ConfirmDialog
        open={confirming}
        onCancel={() => setConfirming(false)}
        onConfirm={runEnrich}
        companies={ENRICH_DEFAULTS.COMPANY_CAP}
        people={ENRICH_DEFAULTS.PEOPLE_CAP}
        busy={busy}
        selectedSteps={selectedSteps}
        onToggleStep={toggleStep}
      />
    </div>
  );
}
